import { useState } from 'react'
import { ChevronDownIcon } from '@radix-ui/react-icons'

interface CompanyPageAccordionProps {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
  stickyHeader?: boolean
  stickyTopOffset?: number
  headerRight?: React.ReactNode
  collapsedContent?: React.ReactNode
  hideChevron?: boolean
  /** Show the chevron but make it non-interactive (e.g. when there's
   *  no content to expand into). Visually fades it and disables the
   *  toggle click. */
  disableToggle?: boolean
  customTitle?: React.ReactNode
  noPaddingBottom?: boolean
  isMobile?: boolean
  /** Tailwind class for the card background. Defaults to bg-gray-50. */
  bgClass?: string
}

export function CompanyPageAccordion({
  title,
  children,
  defaultOpen = true,
  stickyHeader = false,
  stickyTopOffset = 0,
  headerRight,
  collapsedContent,
  hideChevron = false,
  disableToggle = false,
  customTitle,
  noPaddingBottom = false,
  isMobile = false,
  bgClass = 'bg-gray-50',
}: CompanyPageAccordionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div
      className={`${bgClass} rounded-lg border border-gray-200 shadow-md isolate`}
    >
      {/* Accordion Header */}
      <div
        // When the sticky header is the only visible part (collapsed
        // without `collapsedContent`), round its bottom corners too so
        // the card doesn't look square at the bottom edge.
        className={`${stickyHeader ? `sticky ${bgClass} z-40 rounded-t-lg ${!isOpen && !collapsedContent ? 'rounded-b-lg' : ''}` : 'relative'} ${isMobile ? 'p-3' : 'p-4'}`}
        style={
          stickyHeader
            ? {
                top: stickyTopOffset
                  ? `${stickyTopOffset}px`
                  : 'var(--company-top-card-height, 0px)',
              }
            : undefined
        }
      >
        <div className="flex items-center justify-between w-full gap-4">
          {hideChevron ? (
            <div className="flex items-center gap-2">
              {customTitle || (
                <span className="text-sm font-light tracking-wider text-gray-900 uppercase">
                  {title}
                </span>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => !disableToggle && setIsOpen(!isOpen)}
              disabled={disableToggle}
              className="flex items-center gap-2 text-left focus:outline-none disabled:cursor-default"
            >
              <ChevronDownIcon
                className={`w-5 h-5 transition-transform duration-200 ${
                  isOpen ? 'rotate-180' : ''
                } ${disableToggle ? 'text-gray-300' : 'text-green-700'}`}
              />
              {customTitle || (
                <span className="text-sm font-light tracking-wider text-gray-900 uppercase">
                  {title}
                </span>
              )}
            </button>
          )}
          {headerRight && (
            <div className="flex items-center gap-2">{headerRight}</div>
          )}
        </div>
      </div>

      {/* Accordion Content */}
      {(hideChevron || isOpen) && children ? (
        <div
          className={`${isMobile ? 'px-2' : 'px-4'} ${noPaddingBottom ? '' : 'pb-4'}`}
        >
          {children}
        </div>
      ) : !isOpen && collapsedContent ? (
        <div
          role="button"
          tabIndex={0}
          onClick={() => setIsOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setIsOpen(true)
            }
          }}
          className={`${isMobile ? 'px-2' : 'px-4'} pb-4 cursor-pointer focus:outline-none`}
        >
          {collapsedContent}
        </div>
      ) : null}
    </div>
  )
}
